import { useLocaleState, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DEMO_DURATIONS,
  NOT_INTERESTED_REASONS,
} from "../../domain/dialer/outcomes";
import { isAttendeeEmail } from "../../domain/dialer/contactCall"; // GAVION demo-contact
import type { CallOutcome } from "../../domain/dialer/types";
import { zoneParts } from "../../domain/time";
import type { FieldError } from "./dialerReducer";
import {
  CONSENT_OUTCOMES,
  callbackWeekendSuggestion,
  type OutcomeFormState,
} from "./outcomeFormModel";
import { quickChips } from "./quickChips";
import { ERROR_TEXT, clockLabel, dateTimeIn } from "./format";

type Props = {
  outcome: CallOutcome;
  form: OutcomeFormState;
  setForm: (f: OutcomeFormState) => void;
  error: FieldError | null;
  leadEmail: string | null;
  leadTimezone: string;
  now: () => Date;
  callbackTime: string;
};

const pad = (n: number) => String(n).padStart(2, "0");
const toLocal = (iso: string, tz: string) => {
  const p = zoneParts(new Date(iso), tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};
const selectClass = "h-9 w-full rounded-md border bg-background px-2 text-sm";

const Check = ({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(v) => onChange(v === true)}
    />
    <Label htmlFor={id}>{children}</Label>
  </div>
);

/** Inline inputs for the chosen outcome (spec 04 §3 "Required inputs"); shared with the « À compléter » dialog. */
export const OutcomeForm = ({
  outcome,
  form,
  setForm,
  error,
  leadEmail,
  leadTimezone,
  now,
  callbackTime,
}: Props) => {
  const t = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const set = <K extends keyof OutcomeFormState>(
    k: K,
    v: OutcomeFormState[K],
  ) => setForm({ ...form, [k]: v });
  const suggestion = error?.suggestion;
  // GAVION missed-call: a callback typed on a weekend / Dec 25 / Jan 1 is flagged at once, with the next business day
  const weekend =
    outcome === "talked_callback" && error?.code !== "callback_weekend"
      ? callbackWeekendSuggestion(form.callback_local, leadTimezone)
      : undefined;
  return (
    <div className="flex flex-col gap-2">
      {outcome === "talked_callback" && (
        <>
          <Label htmlFor="o-callback">
            {t("gavion.dialer.form.callback_at")}
          </Label>
          <Input
            id="o-callback"
            type="datetime-local"
            value={form.callback_local}
            aria-invalid={error?.field === "callback_at" || undefined}
            onChange={(e) => set("callback_local", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t("gavion.dialer.form.callback_weekdays")}
          </p>
          <div className="flex flex-wrap gap-2">
            {quickChips(now(), leadTimezone, callbackTime).map((c) => (
              <Button
                key={c.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => set("callback_local", c.local)}
              >
                {t(`gavion.dialer.form.chips.${c.key}`, {
                  time: clockLabel(callbackTime, locale),
                })}
              </Button>
            ))}
          </div>
          {weekend && (
            <>
              <p role="alert" className={ERROR_TEXT}>
                {t("gavion.dialer.errors.callback_weekend", {
                  suggestion: dateTimeIn(weekend, leadTimezone, locale),
                })}
              </p>
              <Button
                type="button"
                variant="link"
                className="self-start"
                onClick={() =>
                  set("callback_local", toLocal(weekend, leadTimezone))
                }
              >
                {t("gavion.dialer.errors.use_suggestion")}
              </Button>
            </>
          )}
          {(error?.code === "callback_outside_hours" ||
            error?.code === "callback_weekend") &&
            suggestion && (
              <Button
                type="button"
                variant="link"
                className="self-start"
                onClick={() =>
                  set("callback_local", toLocal(suggestion, leadTimezone))
                }
              >
                {t("gavion.dialer.errors.use_suggestion")}
              </Button>
            )}
        </>
      )}
      {outcome === "demo_booked" && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-demo">{t("gavion.dialer.form.demo_at")}</Label>
            <Input
              id="o-demo"
              type="datetime-local"
              value={form.demo_local}
              onChange={(e) => set("demo_local", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-duration">
              {t("gavion.dialer.form.duration")}
            </Label>
            <select
              id="o-duration"
              className={selectClass}
              value={form.demo_duration}
              onChange={(e) =>
                set("demo_duration", Number(e.target.value) as 30 | 45 | 60)
              }
            >
              {DEMO_DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {t("gavion.dialer.form.minutes", { n: d })}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-attendee">
              {t("gavion.dialer.form.attendee_email")}
            </Label>
            <Input
              id="o-attendee"
              type="email"
              placeholder={leadEmail ?? ""}
              value={form.attendee_email}
              onChange={(e) => set("attendee_email", e.target.value)}
            />
          </div>
          {/* GAVION demo-contact: the attendee becomes a contact of the lead (SQL log_call_outcome, 68_) */}
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-attendee-name">
              {t("gavion.dialer.form.attendee_name")}
            </Label>
            <Input
              id="o-attendee-name"
              autoComplete="off"
              value={form.attendee_name}
              onChange={(e) => set("attendee_name", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-amount">{t("gavion.dialer.form.amount")}</Label>
            <Input
              id="o-amount"
              inputMode="numeric"
              value={form.amount}
              onChange={(e) => set("amount", e.target.value)}
            />
          </div>
          {isAttendeeEmail(form.attendee_email) && (
            <p className="text-xs text-muted-foreground sm:col-span-2">
              {t("gavion.dialer.form.attendee_contact_hint")}
            </p>
          )}
        </div>
      )}
      {outcome === "brochure_sent" && (
        <>
          <Label htmlFor="o-email">{t("gavion.dialer.form.email")}</Label>
          <Input
            id="o-email"
            type="email"
            placeholder={leadEmail ?? ""}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </>
      )}
      {outcome === "not_interested" && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-reason">{t("gavion.dialer.form.reason")}</Label>
            <select
              id="o-reason"
              className={selectClass}
              value={form.reason}
              onChange={(e) => set("reason", e.target.value)}
            >
              <option value="" />
              {NOT_INTERESTED_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`gavion.dialer.form.reasons.${r}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="o-detail">
              {t("gavion.dialer.form.reason_detail")}
            </Label>
            <Input
              id="o-detail"
              value={form.reason_detail}
              onChange={(e) => set("reason_detail", e.target.value)}
            />
          </div>
        </div>
      )}
      {outcome === "do_not_call" && (
        <Check
          id="o-dnc"
          checked={form.dnc_confirmed}
          onChange={(v) => set("dnc_confirmed", v)}
        >
          {t("gavion.dialer.form.dnc_confirm")}
        </Check>
      )}
      {CONSENT_OUTCOMES.includes(outcome) && (
        <Check
          id="o-consent"
          checked={form.email_consent}
          onChange={(v) => set("email_consent", v)}
        >
          {t("gavion.dialer.form.email_consent")}
        </Check>
      )}
      {error && (
        <p role="alert" className={ERROR_TEXT}>
          {t(`gavion.dialer.errors.${error.code}`, {
            suggestion: suggestion
              ? dateTimeIn(suggestion, leadTimezone, locale)
              : "",
          })}
        </p>
      )}
    </div>
  );
};
